<script setup lang="ts">
import { useSlots, ref, watch } from 'vue'
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { useRoute } from 'vue-router'

const props = defineProps<{
  expanded: boolean
  mobileExpanded: boolean
}>()

const emit = defineEmits<{
  (event: 'update:expanded', state: boolean): void
  (event: 'update:mobileExpanded', state: boolean): void
}>()

const route = useRoute()

watch(route, () => {
  emit('update:mobileExpanded', false)
})

const slots = useSlots()
const hasSlot = (name: string) => !!slots[name]
const isTestEnvironment = import.meta.env.VITE_TEST_ENVIRONMENT === 'true'
const isAlphaEnvironment = import.meta.env.VITE_ALPHA_ENVIRONMENT === 'true'
</script>

<template>
  <div class="sticky top-0 left-0 right-0 h-12 flex items-center justify-between px-4 bg-white shadow-bordered md:hidden z-100">
      <router-link :to="{ name: 'control-panel.projects' }" class="flex items-center">
        <img src="/favicon.svg" alt="logo" class="w-6" />
      </router-link>
      <button
        @click="emit('update:mobileExpanded', !mobileExpanded)"
        class="flex items-center text-lg justify-center text-black h-8 w-8 relative">
        <i class="far fa-bars transition-all duration-300 absolute transform"
           :class="mobileExpanded ? 'opacity-0 rotate-90 scale-0' : 'opacity-100 rotate-0 scale-100'" />
        <i class="far fa-xmark transition-all duration-300 absolute transform"
           :class="mobileExpanded ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 rotate-90 scale-0'" />
      </button>

      <!-- Mobile Sidebar -->
      <TransitionRoot as="template" :show="mobileExpanded">
        <Dialog as="div" class="relative z-53 md:hidden" @close="emit('update:mobileExpanded', false)">
          <TransitionChild as="template" enter="transition-opacity ease-linear duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="transition-opacity ease-linear duration-300" leave-from="opacity-100" leave-to="opacity-0">
            <div class="fixed inset-0 top-12 bg-black/50" />
          </TransitionChild>

          <div class="fixed inset-y-0 top-12 left-0 w-screen z-53 flex">
            <TransitionChild as="template" enter="transition ease-in-out duration-300 transform" enter-from="-translate-x-full" enter-to="translate-x-0" leave="transition ease-in-out duration-300 transform" leave-from="translate-x-0" leave-to="-translate-x-full">
              <DialogPanel class="relative flex w-3/4 flex-col bg-white shadow-xl">

                <div class="flex-1 overflow-y-auto">
                  <div class="mt-2">
                    <slot name="top" />
                  </div>
                </div>

                <div
                  v-if="isTestEnvironment && !isAlphaEnvironment"
                  class="bg-softred-500 transition-colors border-gray-100 text-xs py-1.5 text-softred-50 text-center font-bold uppercase">
                  <span v-t="'Test environment'" />
                </div>

                <div
                  v-if="isAlphaEnvironment"
                  class="bg-blue-500 transition-colors border-gray-100 text-xs py-1.5 text-softred-50 text-center font-bold uppercase">
                  <span>ALPHA</span>
                </div>

                <div
                  v-if="hasSlot('bottom')"
                  :class="[
                    'border-gray-100 py-2',
                    { 'border-t': !isTestEnvironment }
                  ]">
                  <slot name="bottom" />
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </TransitionRoot>
    </div>


  <div class="fixed group transition-all duration-300 ease-in-out h-full z-51">
    <!-- Desktop Sidebar -->
    <div id="originalsidebar" :class="[
      'hidden md:fixed md:inset-y-0 md:flex md:flex-col transition-all duration-300 ease-in-out p-3',
      { 'md:w-56': props.expanded },
      { 'md:w-20': !props.expanded }
    ]">
    <button
      @click="emit('update:expanded', !props.expanded)"
      class="absolute flex items-center justify-center w-6 h-6 bg-white rounded-full top-12 right-0 z-51 opacity-0 group-hover:opacity-100 transition-all duration-300 ease-in-out shadow-bordered text-gray-500 hover:bg-primary hover:text-white cursor-pointer">
      <svg width="24" height="24" viewBox="0 0 24 24" role="presentation" :class="[
        { 'rotate-180': props.expanded }
      ]">
        <path d="M10.294 9.698a.988.988 0 010-1.407 1.01 1.01 0 011.419 0l2.965 2.94a1.09 1.09 0 010 1.548l-2.955 2.93a1.01 1.01 0 01-1.42 0 .988.988 0 010-1.407l2.318-2.297-2.327-2.307z" fill="currentColor" fill-rule="evenodd"></path>
      </svg>
    </button>

      <div :class="[
        'flex min-h-0 flex-1 flex-col bg-linear-to-r backdrop-blur-sm rounded-lg shadow-bordered transition-all',
        { ' from-white to-white/70': props.expanded },
        { 'from-white to-white': !props.expanded }
      ]">
        <div class="relative flex flex-1 flex-col overflow-y-auto pb-4 overflow-hidden rounded-t-lg">
          <slot name="logo" v-if="hasSlot('logo')" />
          <div class="mt-2">
            <slot name="top" />
          </div>
        </div>

        <RouterLink
          v-if="isTestEnvironment && !isAlphaEnvironment"
          :to="{ name: 'settings.system' }"
          class="bg-softred-500 hover:bg-softred-600 transition-colors border-gray-100 text-xs py-1.5 text-softred-50 text-center font-bold uppercase">
          <span v-t="'Test'" v-show="!props.expanded"/>
          <span v-t="'Test environment'" v-show="props.expanded"/>
        </RouterLink>

        <RouterLink
          v-if="isAlphaEnvironment"
          :to="{ name: 'settings.system' }"
          class="bg-blue-500 hover:bg-blue-600 transition-colors border-gray-100 text-xs py-1.5 text-softred-50 text-center font-bold uppercase">
          <p>ALPHA</p>
        </RouterLink>

        <div
          v-if="hasSlot('bottom')"
          :class="[
            'border-gray-100 py-2',
            { 'border-t': !isTestEnvironment }
          ]">
          <slot name="bottom" />
        </div>
      </div>
    </div>
  </div>
</template>
